import api from './api';

// Default recognition types to use as fallback
const DEFAULT_RECOGNITION_TYPES = [
  {
    id: 'default-1',
    name: 'Great Work',
    value: 'great_work',
    emoji: '⭐',
    icon: '⭐',
    color_from: '#FFD700',
    color_to: '#FFA500',
    is_active: true,
    display_order: 1,
  },
  {
    id: 'default-2',
    name: 'Team Player',
    value: 'team_player',
    emoji: '🤝',
    icon: '🤝',
    color_from: '#4CAF50',
    color_to: '#45a049',
    is_active: true,
    display_order: 2,
  },
  {
    id: 'default-3',
    name: 'Innovation',
    value: 'innovation',
    emoji: '💡',
    icon: '💡',
    color_from: '#2196F3',
    color_to: '#1976D2',
    is_active: true,
    display_order: 3,
  },
  {
    id: 'default-4',
    name: 'Leadership',
    value: 'leadership',
    emoji: '🏆',
    icon: '🏆',
    color_from: '#9C27B0',
    color_to: '#7B1FA2',
    is_active: true,
    display_order: 4,
  },
  {
    id: 'default-5',
    name: 'Customer Focus',
    value: 'customer_focus',
    emoji: '💖',
    icon: '💖',
    color_from: '#E91E63',
    color_to: '#C2185B',
    is_active: true,
    display_order: 5,
  },
];

const DEFAULT_POINTS_SETTINGS = {
  min_points: 10,
  max_points: 100,
  step: 10,
  points_to_dollar_rate: 1.0,
};

// Helper to normalize backend response to frontend format
const normalizeRecognitionType = (type) => ({
  ...type,
  icon: type.emoji || type.icon,
  value: type.name?.toLowerCase().replace(/\s+/g, '_'),
});

const recognitionTypesService = {
  /**
   * Get recognition types for the current user's organization (public endpoint)
   * Falls back to defaults if API fails
   * @returns {Promise<Array>} - Recognition types list
   */
  getMyRecognitionTypes: async (includePrivate = false) => {
    try {
      const params = includePrivate ? { include_private: true } : {};
      const response = await api.get('/api/v1/recognitions/types', { params });
      return response.data.map(normalizeRecognitionType);
    } catch (error) {
      console.warn('Failed to fetch recognition types, using defaults:', error);
      return DEFAULT_RECOGNITION_TYPES;
    }
  },

  /**
   * Get points settings for the current user's organization (public endpoint)
   * @returns {Promise<object>} - Points settings { min_points, max_points, step, points_to_dollar_rate }
   */
  getPointsSettings: async () => {
    try {
      const response = await api.get('/api/v1/recognitions/points-settings');
      return response.data;
    } catch (error) {
      console.warn('Failed to fetch points settings, using defaults:', error);
      return DEFAULT_POINTS_SETTINGS;
    }
  },

  /**
   * Get recognition types for an organization (admin only)
   * @param {string} organizationId - Organization ID
   * @param {boolean} includeInactive - Include inactive types
   * @returns {Promise<Array>} - Recognition types list
   */
  getByOrganization: async (organizationId, includeInactive = false) => {
    try {
      const params = includeInactive ? { include_inactive: true } : {};
      const response = await api.get(
        `/api/v1/admin/recognition-types/organization/${organizationId}`,
        { params }
      );
      return response.data.map(normalizeRecognitionType);
    } catch (error) {
      console.warn('Failed to fetch recognition types for organization:', error);
      const types = DEFAULT_RECOGNITION_TYPES;
      return includeInactive ? types : types.filter(type => type.is_active);
    }
  },

  /**
   * Create a new recognition type (admin only)
   * @param {object} recognitionTypeData - Recognition type data (includes organization_id)
   * @returns {Promise<object>} - Created recognition type
   */
  create: async (recognitionTypeData) => {
    const organizationId = recognitionTypeData.organization_id;
    const response = await api.post(
      `/api/v1/admin/recognition-types/organization/${organizationId}`,
      {
        name: recognitionTypeData.name,
        emoji: recognitionTypeData.emoji || recognitionTypeData.icon,
        description: recognitionTypeData.description,
        is_active: recognitionTypeData.is_active ?? true,
        display_order: recognitionTypeData.display_order ?? 0,
        color_from: recognitionTypeData.color_from,
        color_to: recognitionTypeData.color_to,
      }
    );
    return normalizeRecognitionType(response.data);
  },

  /**
   * Update a recognition type (admin only)
   * @param {string} recognitionTypeId - Recognition type ID
   * @param {object} recognitionTypeData - Recognition type data to update
   * @returns {Promise<object>} - Updated recognition type
   */
  update: async (recognitionTypeId, recognitionTypeData) => {
    const response = await api.put(
      `/api/v1/admin/recognition-types/${recognitionTypeId}`,
      {
        name: recognitionTypeData.name,
        emoji: recognitionTypeData.emoji || recognitionTypeData.icon,
        description: recognitionTypeData.description,
        is_active: recognitionTypeData.is_active,
        display_order: recognitionTypeData.display_order,
        color_from: recognitionTypeData.color_from,
        color_to: recognitionTypeData.color_to,
      }
    );
    return normalizeRecognitionType(response.data);
  },

  /**
   * Delete a recognition type (admin only)
   * @param {string} recognitionTypeId - Recognition type ID
   * @returns {Promise<object>} - Success message
   */
  delete: async (recognitionTypeId) => {
    const response = await api.delete(
      `/api/v1/admin/recognition-types/${recognitionTypeId}`
    );
    return response.data;
  },

  /**
   * Reorder recognition types (admin only)
   * Updates display_order for each type
   * @param {string} organizationId - Organization ID
   * @param {Array<string>} orderedIds - Array of recognition type IDs in new order
   * @returns {Promise<Array>} - Updated recognition types list
   */
  reorder: async (organizationId, orderedIds) => {
    // Update each recognition type's display_order
    const updates = orderedIds.map((id, index) =>
      api.put(`/api/v1/admin/recognition-types/${id}`, { display_order: index })
    );
    await Promise.all(updates);
    return recognitionTypesService.getByOrganization(organizationId);
  },

  /**
   * Seed default recognition types for an organization (admin only)
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Array>} - Created recognition types list
   */
  seedDefaults: async (organizationId) => {
    // Use the reset endpoint to seed defaults
    return recognitionTypesService.resetToDefaults(organizationId);
  },

  /**
   * Reset recognition types to defaults (admin only)
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Array>} - Default recognition types list
   */
  resetToDefaults: async (organizationId) => {
    const response = await api.post(
      `/api/v1/admin/recognition-types/organization/${organizationId}/reset`
    );
    return response.data.map(normalizeRecognitionType);
  },

  /**
   * Get custom icons for an organization (admin only)
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Array>} - Custom icons list
   */
  getCustomIcons: async (organizationId) => {
    const response = await api.get(
      `/api/v1/admin/recognition-type-icons/organization/${organizationId}`
    );
    return response.data;
  },

  /**
   * Upload a custom icon for recognition types (admin only)
   * @param {string} organizationId - Organization ID
   * @param {File} file - PNG or ICO file
   * @returns {Promise<object>} - Uploaded icon data with url
   */
  uploadCustomIcon: async (organizationId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(
      `/api/v1/admin/recognition-type-icons/organization/${organizationId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  /**
   * Delete a custom icon (admin only)
   * @param {string} iconId - Icon ID
   * @returns {Promise<object>} - Success message
   */
  deleteCustomIcon: async (iconId) => {
    const response = await api.delete(
      `/api/v1/admin/recognition-type-icons/${iconId}`
    );
    return response.data;
  },
};

export default recognitionTypesService;
